import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import UPButton from 'components/button';
import {
  MFA_ENROLLMENT__BACKUP_CODES_TITLE,
  MFA_ENROLLMENT__BACKUP_CODES_WARNING,
  MFA_ENROLLMENT__CODES_COPIED,
  MFA_ENROLLMENT__CONFIRM,
  MFA_ENROLLMENT__COPY_CODES,
  MFA_ENROLLMENT__DOWNLOAD_CODES,
} from 'translations/constants';
import { MfaCardTitle, MfaHint, MfaInstructions } from '../mfa-styles';
import { MfaCode, MfaCodesActions, MfaCodesGrid } from './styles';

interface MfaBackupCodesViewProps {
  codes: string[];
  onConfirm: () => void;
}

/** Not translated: it is a filename, and a stable one is easier to talk about in support. */
const CODES_FILE_NAME = 'uportal-backup-codes.txt';

/**
 * The only time these codes are ever visible. They are stored hashed, so nothing can show them
 * again, and there is no reset endpoint — which is why Copy and Download are here rather than
 * leaving the user to transcribe eight codes by hand.
 *
 * There is deliberately no back link. The enrollment is already committed by the time this renders,
 * so going back would re-submit a code against an existing enrollment and earn a 409.
 */
const MfaBackupCodesView = (props: MfaBackupCodesViewProps) => {
  const { codes, onConfirm } = props;
  const [t] = useTranslation();
  const [copied, setCopied] = useState(false);

  const asText = useMemo(() => codes.join('\n'), [codes]);

  const copyCodes = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(asText);
      setCopied(true);
    } catch {
      // Clipboard access can be denied; the codes are on screen and selectable, and Download remains.
    }
  }, [asText]);

  const downloadCodes = useCallback(() => {
    const url = URL.createObjectURL(new Blob([asText], { type: 'text/plain' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = CODES_FILE_NAME;
    link.click();
    URL.revokeObjectURL(url);
  }, [asText]);

  return (
    <>
      <MfaCardTitle>{t(MFA_ENROLLMENT__BACKUP_CODES_TITLE)}</MfaCardTitle>
      <MfaInstructions>{t(MFA_ENROLLMENT__BACKUP_CODES_WARNING)}</MfaInstructions>
      <MfaCodesGrid>
        {codes.map(code => (
          <MfaCode key={code}>{code}</MfaCode>
        ))}
      </MfaCodesGrid>
      <MfaCodesActions>
        <UPButton text={t(MFA_ENROLLMENT__COPY_CODES)} onClick={copyCodes} outlined />
        <UPButton text={t(MFA_ENROLLMENT__DOWNLOAD_CODES)} onClick={downloadCodes} outlined />
      </MfaCodesActions>
      {copied && <MfaHint>{t(MFA_ENROLLMENT__CODES_COPIED)}</MfaHint>}
      <UPButton text={t(MFA_ENROLLMENT__CONFIRM)} onClick={onConfirm} />
    </>
  );
};

export default MfaBackupCodesView;
